import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";

type Client = SupabaseClient<Database>;

export interface UserActivityRow {
  user_id: string;
  email: string | null;
  display_name: string | null;
  plan: "pending" | "beta" | "blocked";
  role: "user" | "admin";
  created_at: string;
  last_login_at: string | null;
  last_use_at: string | null;
  days_inactive: number | null;
  events_7d: number;
  events_30d: number;
  cost_usd_7d: number;
  cost_usd_30d: number;
  tokens_7d: number;
  top_feature_7d: string | null;
}

export interface ActivityDetailFeatureRow {
  feature: string;
  events: number;
  cost_usd: number;
  tokens: number;
}

export interface ActivityDetailModelRow {
  model_id: string;
  provider: string;
  events: number;
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
}

export interface ActivityDetailDailyRow {
  day: string;
  events: number;
  cost_usd: number;
}

export interface UserActivityDetail {
  profile: {
    id: string;
    email: string | null;
    display_name: string | null;
    plan: string;
    role: string;
    created_at: string;
  } | null;
  last_login_at: string | null;
  last_use_at: string | null;
  totals: {
    events: number;
    cost_usd: number;
    tokens_in: number;
    tokens_out: number;
  };
  by_feature: ActivityDetailFeatureRow[];
  by_model: ActivityDetailModelRow[];
  daily: ActivityDetailDailyRow[];
}

export async function fetchUserActivity(client: Client): Promise<UserActivityRow[]> {
  const { data, error } = await client.rpc("admin_user_activity");
  if (error) throw error;
  return (data ?? []) as UserActivityRow[];
}

export async function fetchUserActivityDetail(
  client: Client,
  userId: string,
  windowDays = 30,
): Promise<UserActivityDetail> {
  const { data, error } = await client.rpc("admin_user_activity_detail", {
    target_user: userId,
    window_days: windowDays,
  });
  if (error) throw error;
  return data as unknown as UserActivityDetail;
}
